import {
  ALL_DENIED,
  CATEGORY_BY_TC_ID,
  ConsentDecision,
  createDecision,
} from './categories';

export const CONSENT_COOKIE_V2 = 'TC_PRIVACY_CENTER_V2';
export const CONSENT_COOKIE_V1 = 'TC_PRIVACY_CENTER';

type StoredConsentV2 = {
  categories: ConsentDecision;
};

type CookieReader = (name: string) => string | undefined;

export const readConsent = (readCookie: CookieReader): ConsentDecision | null =>
  parseConsentV2(readCookie(CONSENT_COOKIE_V2)) ??
  parseConsentV1(readCookie(CONSENT_COOKIE_V1));

export const readClientConsent = (): ConsentDecision | null =>
  readConsent(readBrowserCookie);

const readBrowserCookie = (name: string): string | undefined => {
  if (typeof document === 'undefined') {
    return undefined;
  }

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split(';')
    .map(entry => entry.trim())
    .find(entry => entry.startsWith(prefix));

  return cookie?.slice(prefix.length);
};

export const parseConsentV2 = (
  raw: string | undefined,
): ConsentDecision | null => {
  if (!raw?.trim()) {
    return null;
  }

  try {
    const parsed = JSON.parse(
      decodeURIComponent(raw.trim()),
    ) as Partial<StoredConsentV2>;

    if (!parsed.categories) {
      return null;
    }

    return createDecision({
      analytical: Boolean(parsed.categories.analytical),
      marketing: Boolean(parsed.categories.marketing),
      personalization: Boolean(parsed.categories.personalization),
    });
  } catch {
    return null;
  }
};

export const parseConsentV1 = (
  raw: string | undefined,
): ConsentDecision | null => {
  if (raw === undefined) {
    return null;
  }

  if (!raw.trim()) {
    return ALL_DENIED;
  }

  let value = raw.trim();
  try {
    value = decodeURIComponent(value);
  } catch {
    // Keep the raw value so malformed encoding still yields a safe decision.
  }

  const categories = value
    .split(',')
    .reduce<Partial<ConsentDecision>>((decision, id) => {
      const category = CATEGORY_BY_TC_ID[id.trim()];

      if (category && category !== 'essential') {
        decision[category] = true;
      }

      return decision;
    }, {});

  return createDecision(categories);
};